<template>
  <v-container fluid>
    <v-layout column>
      <!-- Templates (marketing doc 2026-08-18 §3.3) -->
      <v-flex xs12>
        <v-card>
          <v-card-title primary-title>
            <div>
              <div class="headline">{{ $t("passengersPage.mkTemplatesTitle") }}</div>
              <div class="grey--text text--darken-1" style="font-size: 13px;">
                {{ $t("passengersPage.mkTemplatesHint") }}
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap align-center>
              <v-flex xs12 md4>
                <v-select
                  v-model="selectedTemplate"
                  :items="templates"
                  item-text="name"
                  return-object
                  :label='$t("passengersPage.loadPreset")'
                  :no-data-text='$t("passengersPage.noPresets")'
                  clearable
                  @change="applyTemplate"
                ></v-select>
              </v-flex>
              <v-flex xs12 md8 class="text-xs-left">
                <v-btn small color="primary" @click="openTemplateDialog(false)">
                  {{ $t("passengersPage.mkSaveAsTemplate") }}
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  outline
                  v-if="selectedTemplate && selectedTemplate.id"
                  :loading="templateSaving"
                  @click="updateTemplate"
                >
                  {{ $t("passengersPage.mkUpdateTemplate") }}
                </v-btn>
                <v-btn
                  small
                  color="red"
                  flat
                  v-if="selectedTemplate && selectedTemplate.id"
                  @click="deleteTemplate"
                >
                  {{ $t("passengersPage.deletePreset") }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Audience (doc §3.1/§3.2): send-to-all default, simplified filter,
           specific-user picker -->
      <v-flex xs12 class="mt-4">
        <v-card>
          <v-card-title primary-title>
            <div class="headline">{{ $t("passengersPage.mkAudienceTitle") }}</div>
          </v-card-title>
          <v-card-text>
            <!--
              Custom selectable cards instead of v-radio-group: interleaving
              expandable bodies between v-radio children broke the RTL layout
              (the checked circle vanished), and the font-icon radio glyph is
              avoided entirely — the dot is pure CSS.
            -->
            <div
              class="mode-option"
              :class="{ 'mode-active': targetingMode === 'ALL' }"
              @click="targetingMode = 'ALL'"
            >
              <div class="mode-head">
                <span class="mode-dot"></span>
                <span class="mode-title">{{ $t("passengersPage.mkSendToAll") }}</span>
              </div>
              <div v-if="targetingMode === 'ALL'" class="mode-desc">
                {{ $t("passengersPage.mkSendToAllHint") }}
              </div>
            </div>

            <div
              class="mode-option"
              :class="{ 'mode-active': targetingMode === 'FILTERED' }"
              @click="targetingMode = 'FILTERED'"
            >
              <div class="mode-head">
                <span class="mode-dot"></span>
                <span class="mode-title">{{ $t("passengersPage.mkFilterMode") }}</span>
              </div>
              <div v-if="targetingMode === 'FILTERED'" class="mode-content" @click.stop>
                <v-layout row wrap>
                  <v-flex xs12 sm6 md3 class="field-col">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-on="on"
                          v-model="filter.registered_from"
                          :label='$t("passengersPage.registeredFrom")'
                          type="date"
                          clearable
                        ></v-text-field>
                      </template>
                      <span>{{ $t("passengersPage.mkRegisteredFromTip") }}</span>
                    </v-tooltip>
                  </v-flex>
                  <v-flex xs12 sm6 md3 class="field-col">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-on="on"
                          v-model="filter.registered_to"
                          :label='$t("passengersPage.registeredTo")'
                          type="date"
                          clearable
                        ></v-text-field>
                      </template>
                      <span>{{ $t("passengersPage.mkRegisteredToTip") }}</span>
                    </v-tooltip>
                  </v-flex>
                  <v-flex xs12 sm6 md3 class="field-col">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-select
                          v-on="on"
                          v-model="filter.activity"
                          :items="activityOptions"
                          :label='$t("passengersPage.mkActivity")'
                          clearable
                        ></v-select>
                      </template>
                      <span>{{ $t("passengersPage.mkActivityTip", { days: filter.activity_days || 30 }) }}</span>
                    </v-tooltip>
                  </v-flex>
                  <v-flex xs12 sm6 md3 class="field-col" v-if="filter.activity">
                    <v-text-field
                      v-model.number="filter.activity_days"
                      :label='$t("passengersPage.mkActivityDays")'
                      type="number"
                      min="1"
                      :hint='$t("passengersPage.mkActivityDaysHint")'
                      persistent-hint
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </div>
            </div>

            <div
              class="mode-option"
              :class="{ 'mode-active': targetingMode === 'SPECIFIC' }"
              @click="targetingMode = 'SPECIFIC'"
            >
              <div class="mode-head">
                <span class="mode-dot"></span>
                <span class="mode-title">{{ $t("passengersPage.mkSpecificUsers") }}</span>
              </div>
              <div v-if="targetingMode === 'SPECIFIC'" class="mode-content" @click.stop>
                <v-layout row wrap align-center>
                  <v-flex xs12 sm8 md6 class="field-col">
                    <v-text-field
                      v-model="search.q"
                      :label='$t("passengersPage.mkSearchUsers")'
                      prepend-icon="search"
                      clearable
                      @keyup.enter="searchUsers"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm4 md2 class="field-col">
                    <v-btn small color="primary" :loading="search.loading" @click="searchUsers">
                      {{ $t("passengersPage.mkSearch") }}
                    </v-btn>
                  </v-flex>
                </v-layout>

                <div v-if="search.results.length" class="search-results">
                  <v-list dense>
                    <v-list-tile v-for="user in search.results" :key="user.type + '-' + user.id">
                      <v-list-tile-content>
                        <v-list-tile-title>
                          {{ user.name || "—" }}
                          <span class="grey--text">({{ user.phone_number || "-" }})</span>
                        </v-list-tile-title>
                      </v-list-tile-content>
                      <v-list-tile-action class="tile-action-row">
                        <v-chip small :color="user.type === 'CAPTAIN' ? 'orange lighten-4' : 'blue lighten-4'">
                          {{ user.type === "CAPTAIN" ? $t("passengersPage.captain") : $t("passengersPage.passenger") }}
                        </v-chip>
                        <v-btn
                          small
                          flat
                          color="primary"
                          :disabled="isSelected(user)"
                          @click="addUser(user)"
                        >
                          {{ $t("passengersPage.mkAddUser") }}
                        </v-btn>
                      </v-list-tile-action>
                    </v-list-tile>
                  </v-list>
                </div>
                <div v-else-if="search.performed && !search.loading" class="grey--text text--darken-1 mt-2">
                  {{ $t("passengersPage.mkSearchNoResults") }}
                </div>

                <div class="selected-users-box">
                  <div class="subheading">
                    {{ $t("passengersPage.mkSelectedUsers") }} ({{ selectedUsers.length }})
                  </div>
                  <div v-if="!selectedUsers.length" class="grey--text text--darken-1">
                    {{ $t("passengersPage.mkNoSelectedUsers") }}
                  </div>
                  <v-chip
                    v-for="user in selectedUsers"
                    :key="user.type + '-' + user.id"
                    close
                    @input="removeUser(user)"
                    class="ma-1"
                  >
                    {{ user.name || user.id }} ({{ user.phone_number || "-" }})
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Audience type (hidden for SPECIFIC — derived from the picks) -->
            <div v-if="targetingMode !== 'SPECIFIC'" class="audience-types">
              <v-layout row wrap>
                <v-flex xs12 sm6>
                  <v-checkbox v-model="form.include_passengers" hide-details class="mt-0">
                    <template v-slot:label>
                      <span>{{ $t("passengersPage.passengers") }} <strong>({{ passengersCountLabel }})</strong></span>
                    </template>
                  </v-checkbox>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-checkbox v-model="form.include_drivers" hide-details class="mt-0">
                    <template v-slot:label>
                      <span>{{ $t("passengersPage.driversSnapCaptain") }} <strong>({{ driversCountLabel }})</strong></span>
                    </template>
                  </v-checkbox>
                </v-flex>
              </v-layout>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout align-center justify-space-between style="width: 100%;">
              <div>
                {{ $t("passengersPage.mkRecipientsCount") }}
                <strong>{{ totalCountLabel }}</strong>
                <v-progress-circular
                  v-if="countLoading"
                  indeterminate
                  size="14"
                  width="2"
                  color="primary"
                  class="mx-2"
                ></v-progress-circular>
              </div>
              <v-btn small flat color="primary" @click="openRecipients">
                {{ $t("passengersPage.mkViewList") }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-flex>

      <!-- Notification content (doc §4) -->
      <v-flex xs12 class="mt-4">
        <v-card>
          <v-card-title primary-title>
            <div class="headline">{{ $t("passengersPage.mkContentTitle") }}</div>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="form.title"
              :label='$t("passengersPage.mkNotificationTitle")'
              counter="191"
            ></v-text-field>
            <v-textarea
              v-model="form.message"
              :label='$t("passengersPage.mkNotificationText")'
              auto-grow
              rows="3"
            ></v-textarea>
            <v-text-field
              v-model="form.url"
              :label='$t("passengersPage.mkNotificationUrl")'
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Delivery options (doc §3.4) -->
      <v-flex xs12 class="mt-4">
        <v-card>
          <v-card-title primary-title>
            <div class="headline">{{ $t("passengersPage.mkOptionsTitle") }}</div>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 sm4>
                <v-checkbox
                  v-model="form.send_push"
                  :label='$t("passengersPage.mkChannelPushLabel")'
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 sm4>
                <v-checkbox
                  v-model="form.send_in_app"
                  :label='$t("passengersPage.mkChannelInAppLabel")'
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 sm4>
                <v-checkbox
                  v-model="form.send_sms"
                  :label='$t("passengersPage.mkChannelSmsLabel")'
                ></v-checkbox>
              </v-flex>
              <v-flex xs12>
                <v-checkbox
                  v-model="form.deferred"
                  :label='$t("passengersPage.mkDeferred")'
                  :hint='$t("passengersPage.mkDeferredHint")'
                  persistent-hint
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 class="mt-2">
                <v-checkbox
                  v-model="schedule.enabled"
                  :label='$t("passengersPage.scheduleLater")'
                  :hint='$t("passengersPage.scheduleHint")'
                  persistent-hint
                ></v-checkbox>
              </v-flex>
              <v-flex xs12 sm6 v-if="schedule.enabled">
                <v-text-field
                  v-model="schedule.datetime"
                  :label='$t("passengersPage.scheduleDateTime")'
                  type="datetime-local"
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="!canSend"
              :loading="sending"
              @click="confirmSend"
            >
              {{ $t("passengersPage.send") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>

      <!-- Campaign history with live delivery status (doc §3.4) -->
      <v-flex xs12 class="mt-4">
        <v-card>
          <v-card-title primary-title>
            <div class="headline">{{ $t("passengersPage.campaignHistory") }}</div>
            <v-spacer></v-spacer>
            <v-btn small flat color="primary" :loading="campaignsLoading" @click="loadCampaigns">
              {{ $t("passengersPage.refreshHistory") }}
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="!campaigns.length" class="grey--text text--darken-1">
              {{ $t("passengersPage.noCampaigns") }}
            </div>
            <div v-else style="overflow-x: auto;">
              <table class="v-table campaign-history">
                <thead>
                  <tr>
                    <th>{{ $t("passengersPage.campaignDate") }}</th>
                    <th>{{ $t("passengersPage.campaignStatus") }}</th>
                    <th>{{ $t("passengersPage.campaignChannels") }}</th>
                    <th>{{ $t("passengersPage.campaignTargeted") }}</th>
                    <th>{{ $t("passengersPage.mkDeliveryStats") }}</th>
                    <th>{{ $t("passengersPage.campaignFilters") }}</th>
                    <th>{{ $t("passengersPage.campaignMessage") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in campaigns" :key="c.id || i">
                    <td>{{ campaignDateLabel(c) }}</td>
                    <td>{{ campaignStatusLabel(c) }}</td>
                    <td>{{ campaignChannelsLabel(c) }}</td>
                    <td>{{ campaignTargetedLabel(c) }}</td>
                    <td>{{ deliveryStatsLabel(c) }}</td>
                    <td style="max-width: 220px;">{{ filtersSummary(c.filters) }}</td>
                    <td style="max-width: 260px;">{{ campaignMessageLabel(c) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Save-as-template dialog -->
    <v-dialog v-model="templateDialog" max-width="420">
      <v-card>
        <v-card-title class="headline">{{ $t("passengersPage.mkSaveAsTemplate") }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="templateName"
            :label='$t("passengersPage.presetName")'
            autofocus
          ></v-text-field>
          <v-checkbox
            v-model="templateShared"
            :label='$t("passengersPage.mkTemplateShared")'
            :hint='$t("passengersPage.mkTemplateSharedHint")'
            persistent-hint
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="templateDialog = false">{{ $t("passengersPage.mkCancel") }}</v-btn>
          <v-btn color="primary" :loading="templateSaving" @click="saveTemplate">
            {{ $t("passengersPage.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Recipients list dialog (doc §3.1: dynamic count + viewable list) -->
    <v-dialog v-model="recipients.open" max-width="640" scrollable>
      <v-card>
        <v-card-title class="headline">
          {{ $t("passengersPage.mkRecipientsTitle") }} ({{ recipients.total }})
          <v-spacer></v-spacer>
          <v-btn small flat color="primary" :loading="recipients.exporting" @click="exportRecipients">
            {{ $t("passengersPage.mkExportCsv") }}
          </v-btn>
        </v-card-title>
        <v-card-text style="max-height: 420px;">
          <v-layout row wrap align-center>
            <v-flex xs12 sm6>
              <v-select
                v-model="recipients.audience"
                :items="recipientsAudienceOptions"
                @change="loadRecipients(1)"
              ></v-select>
            </v-flex>
          </v-layout>
          <div v-if="recipients.loading" class="text-xs-center py-3">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <table v-else class="v-table campaign-history" style="width: 100%;">
            <thead>
              <tr>
                <th>{{ $t("passengersPage.id") }}</th>
                <th>{{ $t("passengersPage.fullName") }}</th>
                <th>{{ $t("passengersPage.phoneNumber") }}</th>
                <th>{{ $t("passengersPage.type") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in recipients.items" :key="row.type + '-' + row.id">
                <td>{{ row.id }}</td>
                <td>{{ row.name || "—" }}</td>
                <td>{{ row.phone_number || "-" }}</td>
                <td>{{ row.type === "CAPTAIN" ? $t("passengersPage.captain") : $t("passengersPage.passenger") }}</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions v-if="recipientsPages > 1">
          <v-spacer></v-spacer>
          <v-pagination
            v-model="recipients.page"
            :length="recipientsPages"
            :total-visible="7"
            @input="loadRecipients"
          ></v-pagination>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "PassengersMarketing",
  title: "passengersPage.marketingTitle",
  data() {
    return {
      // Audience (doc §3.1): send-to-all is the default.
      targetingMode: "ALL",
      filter: {
        registered_from: null,
        registered_to: null,
        activity: null,
        activity_days: 30,
      },
      selectedUsers: [],
      search: {
        q: "",
        loading: false,
        performed: false,
        results: [],
      },
      form: {
        title: "",
        message: "",
        url: "",
        send_push: true,
        send_in_app: false,
        send_sms: false,
        deferred: true,
        include_passengers: true,
        include_drivers: false,
      },
      schedule: {
        enabled: false,
        datetime: null,
      },
      countLoading: false,
      countTimer: null,
      sending: false,
      passengersCount: null,
      driversCount: null,
      // Templates
      templates: [],
      selectedTemplate: null,
      templateDialog: false,
      templateName: "",
      templateShared: true,
      templateSaving: false,
      // Recipients dialog
      recipients: {
        open: false,
        audience: "both",
        page: 1,
        perPage: 50,
        total: 0,
        items: [],
        loading: false,
        exporting: false,
      },
      // History
      campaigns: [],
      campaignsLoading: false,
    };
  },
  computed: {
    activityOptions() {
      return [
        { text: this.$t("passengersPage.mkActivityActive"), value: "active" },
        { text: this.$t("passengersPage.mkActivityInactive"), value: "inactive" },
      ];
    },
    recipientsAudienceOptions() {
      // "both" is the merged passengers+drivers list (the default), so a
      // mixed specific-users selection shows every recipient.
      return [
        { text: this.$t("passengersPage.all"), value: "both" },
        { text: this.$t("passengersPage.passengers"), value: "passengers" },
        { text: this.$t("passengersPage.drivers"), value: "drivers" },
      ];
    },
    selectedPassengerIds() {
      return this.selectedUsers.filter((u) => u.type === "PASSENGER").map((u) => u.id);
    },
    selectedCaptainIds() {
      return this.selectedUsers.filter((u) => u.type === "CAPTAIN").map((u) => u.id);
    },
    canSend() {
      const hasChannel = this.form.send_push || this.form.send_in_app || this.form.send_sms;
      const hasAudience =
        this.targetingMode === "SPECIFIC"
          ? this.selectedUsers.length > 0
          : this.form.include_passengers || this.form.include_drivers;
      return !!this.form.message && hasChannel && hasAudience;
    },
    passengersCountLabel() {
      return this.passengersCount === null ? "—" : this.passengersCount;
    },
    driversCountLabel() {
      return this.driversCount === null ? "—" : this.driversCount;
    },
    totalCountLabel() {
      if (this.passengersCount === null && this.driversCount === null) return "—";
      let total = 0;
      if (this.targetingMode === "SPECIFIC") {
        total = (this.passengersCount || 0) + (this.driversCount || 0);
      } else {
        if (this.form.include_passengers) total += this.passengersCount || 0;
        if (this.form.include_drivers) total += this.driversCount || 0;
      }
      return total;
    },
    recipientsPages() {
      return Math.max(1, Math.ceil(this.recipients.total / this.recipients.perPage));
    },
    // Single serialized source watched for the dynamic recipient count.
    countKey() {
      return JSON.stringify(this.filterPayload());
    },
  },
  watch: {
    // Doc §3.1: the recipients count updates immediately (debounced) whenever
    // any audience input changes — no manual refresh button.
    countKey() {
      if (this.countTimer) clearTimeout(this.countTimer);
      this.countTimer = setTimeout(() => this.simulateCount(), 450);
    },
  },
  methods: {
    // Single source for the audience payload, shared by preview, recipients
    // listing and the send call so the count always matches what is sent.
    filterPayload() {
      const payload = {
        targeting_mode: this.targetingMode,
        include_passengers: this.form.include_passengers,
        include_drivers: this.form.include_drivers,
      };
      if (this.targetingMode === "FILTERED") {
        payload.registered_from = this.filter.registered_from || undefined;
        payload.registered_to = this.filter.registered_to || undefined;
        payload.activity = this.filter.activity || undefined;
        if (this.filter.activity) {
          payload.activity_days = this.filter.activity_days || 30;
        }
      }
      if (this.targetingMode === "SPECIFIC") {
        payload.selected_passenger_ids = this.selectedPassengerIds;
        payload.selected_captain_ids = this.selectedCaptainIds;
        payload.include_passengers = this.selectedPassengerIds.length > 0;
        payload.include_drivers = this.selectedCaptainIds.length > 0;
      }
      return payload;
    },
    async simulateCount() {
      this.countLoading = true;
      try {
        const { data } = await this.axios.post(
          this.api.ACTIONS.PASSENGERS_MARKETING_PREVIEW,
          this.filterPayload()
        );
        this.passengersCount = data.data.count || 0;
        this.driversCount = data.data.drivers_count || 0;
      } catch (e) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.countLoadFailed"),
          "red"
        );
      } finally {
        this.countLoading = false;
      }
    },

    // ---- Specific users (doc §3.2) ----
    async searchUsers() {
      if (!this.search.q) return;
      this.search.loading = true;
      this.search.performed = true;
      try {
        const { data } = await this.axios.post(
          this.api.ACTIONS.PASSENGERS_MARKETING_SEARCH_USERS,
          { q: this.search.q, audience: "both" }
        );
        this.search.results = (data && data.data) || [];
      } catch (e) {
        this.search.results = [];
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.mkSearchFailed"),
          "red"
        );
      } finally {
        this.search.loading = false;
      }
    },
    isSelected(user) {
      return this.selectedUsers.some((u) => u.id === user.id && u.type === user.type);
    },
    addUser(user) {
      if (!this.isSelected(user)) this.selectedUsers.push(user);
    },
    removeUser(user) {
      this.selectedUsers = this.selectedUsers.filter(
        (u) => !(u.id === user.id && u.type === user.type)
      );
    },

    // ---- Recipients list dialog (doc §3.1) ----
    openRecipients() {
      this.recipients.audience = "both";
      this.recipients.open = true;
      this.loadRecipients(1);
    },
    async loadRecipients(page) {
      this.recipients.loading = true;
      this.recipients.page = page || 1;
      try {
        const { data } = await this.axios.post(
          this.api.ACTIONS.PASSENGERS_MARKETING_RECIPIENTS,
          {
            ...this.filterPayload(),
            audience: this.recipients.audience,
            page: this.recipients.page,
            per_page: this.recipients.perPage,
          }
        );
        this.recipients.total = data.data.total || 0;
        this.recipients.items = data.data.items || [];
      } catch (e) {
        this.recipients.items = [];
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.countLoadFailed"),
          "red"
        );
      } finally {
        this.recipients.loading = false;
      }
    },
    async exportRecipients() {
      this.recipients.exporting = true;
      try {
        const rows = [];
        const perPage = 200;
        // Bounded export: up to 25 pages (5000 rows) per audience.
        const maxPages = 25;
        for (let page = 1; page <= maxPages; page++) {
          const { data } = await this.axios.post(
            this.api.ACTIONS.PASSENGERS_MARKETING_RECIPIENTS,
            {
              ...this.filterPayload(),
              audience: this.recipients.audience,
              page,
              per_page: perPage,
            }
          );
          const items = data.data.items || [];
          rows.push(...items);
          if (page * perPage >= (data.data.total || 0) || !items.length) break;
        }
        const header = ["id", "type", "name", "phone_number"];
        // UTF-8 BOM so Excel opens Arabic names correctly.
        const csv =
          "﻿" +
          [header.join(",")]
            .concat(
              rows.map((r) =>
                header
                  .map((h) => `"${String(r[h] == null ? "" : r[h]).replace(/"/g, '""')}"`)
                  .join(",")
              )
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `marketing-recipients-${this.recipients.audience}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.countLoadFailed"),
          "red"
        );
      } finally {
        this.recipients.exporting = false;
      }
    },

    // ---- Send ----
    confirmSend() {
      const audienceParts = [];
      if (this.targetingMode === "SPECIFIC") {
        audienceParts.push(
          this.$t("passengersPage.mkSpecificUsersCount", {
            count: this.selectedUsers.length,
          })
        );
      } else {
        if (this.form.include_passengers) {
          audienceParts.push(
            this.targetingMode === "FILTERED"
              ? this.$t("passengersPage.filteredPassengers")
              : this.$t("passengersPage.allActivePassengers")
          );
        }
        if (this.form.include_drivers) {
          audienceParts.push(this.$t("passengersPage.drivers"));
        }
      }
      const channels = [
        this.form.send_push ? this.$t("passengersPage.channelPush") : null,
        this.form.send_in_app ? this.$t("passengersPage.mkChannelInApp") : null,
        this.form.send_sms ? this.$t("passengersPage.channelSms") : null,
      ]
        .filter(Boolean)
        .join(" + ");

      this.$dialova({
        title: this.$t("passengersPage.sendCampaignTitle"),
        paragraph: this.$t("passengersPage.sendCampaignConfirm", {
          channels,
          target: audienceParts.join(" + "),
        }),
        loader: true,
      })
        .then(async ({ close }) => {
          this.sending = true;
          try {
            const payload = {
              ...this.filterPayload(),
              title: this.form.title || undefined,
              message: this.form.message,
              url: this.form.url || undefined,
              send_sms: this.form.send_sms,
              send_notification: this.form.send_push,
              send_in_app: this.form.send_in_app,
              deferred: this.form.deferred,
            };
            if (this.schedule.enabled && this.schedule.datetime) {
              payload.scheduled_at = this.schedule.datetime;
            }
            await this.axios.post(
              this.api.ACTIONS.PASSENGERS_MARKETING_SEND,
              payload
            );
            window.getApp.$emit(
              "SHOW_TOAST",
              this.$t("passengersPage.campaignQueued"),
              "green"
            );
            this.loadCampaigns();
          } catch (e) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.$t("passengersPage.campaignFailed"),
              "red"
            );
          } finally {
            this.sending = false;
            close();
          }
        })
        .catch(() => {
          // cancelled
        });
    },

    // ---- Templates (doc §3.3): full page state, shared or private ----
    templatePayload() {
      return {
        ...this.filterPayload(),
        selected_users: this.selectedUsers,
        content: {
          title: this.form.title,
          message: this.form.message,
          url: this.form.url,
        },
        channels: {
          push: this.form.send_push,
          in_app: this.form.send_in_app,
          sms: this.form.send_sms,
          deferred: this.form.deferred,
        },
      };
    },
    applyTemplate(template) {
      if (!template || !template.filters) return;
      const f = template.filters;
      this.targetingMode = f.targeting_mode || "ALL";
      // Default templates use a relative window; surface it as concrete dates.
      this.filter.registered_from = f.registered_within_days
        ? this.$companyTime(this.moment().subtract(f.registered_within_days, "days"), "YYYY-MM-DD")
        : f.registered_from || null;
      this.filter.registered_to = f.registered_to || null;
      this.filter.activity = f.activity || null;
      this.filter.activity_days = f.activity_days || 30;
      this.selectedUsers = Array.isArray(f.selected_users) ? f.selected_users : [];
      if (typeof f.include_passengers === "boolean") {
        this.form.include_passengers = f.include_passengers;
      }
      if (typeof f.include_drivers === "boolean") {
        this.form.include_drivers = f.include_drivers;
      }
      if (f.content) {
        this.form.title = f.content.title || "";
        this.form.message = f.content.message || "";
        this.form.url = f.content.url || "";
      }
      if (f.channels) {
        this.form.send_push = f.channels.push !== false;
        this.form.send_in_app = !!f.channels.in_app;
        this.form.send_sms = !!f.channels.sms;
        this.form.deferred = f.channels.deferred !== false;
      }
    },
    async loadTemplates() {
      try {
        const { data } = await this.axios.get(
          this.api.ACTIONS.PASSENGERS_MARKETING_PRESETS
        );
        const payload = (data && data.data) || [];
        this.templates = Array.isArray(payload) ? payload : payload.data || [];
      } catch (e) {
        this.templates = [];
      }
    },
    openTemplateDialog() {
      this.templateName = "";
      this.templateShared = true;
      this.templateDialog = true;
    },
    async saveTemplate() {
      if (!this.templateName) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.presetNameRequired"),
          "red"
        );
        return;
      }
      this.templateSaving = true;
      try {
        await this.axios.post(this.api.ACTIONS.PASSENGERS_MARKETING_PRESETS, {
          name: this.templateName,
          filters: this.templatePayload(),
          is_shared: this.templateShared,
        });
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.presetSaved"),
          "green"
        );
        this.templateDialog = false;
        this.loadTemplates();
      } catch (e) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.presetSaveFailed"),
          "red"
        );
      } finally {
        this.templateSaving = false;
      }
    },
    async updateTemplate() {
      if (!this.selectedTemplate || !this.selectedTemplate.id) return;
      this.templateSaving = true;
      try {
        await this.axios.put(
          this.api.ACTIONS.PASSENGERS_MARKETING_PRESET(this.selectedTemplate.id),
          { filters: this.templatePayload() }
        );
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.mkTemplateUpdated"),
          "green"
        );
        this.loadTemplates();
      } catch (e) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.mkTemplateUpdateFailed"),
          "red"
        );
      } finally {
        this.templateSaving = false;
      }
    },
    async deleteTemplate() {
      if (!this.selectedTemplate || !this.selectedTemplate.id) return;
      try {
        await this.axios.delete(
          this.api.ACTIONS.PASSENGERS_MARKETING_PRESET(this.selectedTemplate.id)
        );
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.presetDeleted"),
          "green"
        );
        this.selectedTemplate = null;
        this.loadTemplates();
      } catch (e) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("passengersPage.presetDeleteFailed"),
          "red"
        );
      }
    },

    // ---- Campaign history ----
    async loadCampaigns() {
      this.campaignsLoading = true;
      try {
        const { data } = await this.axios.get(
          this.api.ACTIONS.PASSENGERS_MARKETING_CAMPAIGNS
        );
        const payload = (data && data.data) || [];
        this.campaigns = Array.isArray(payload) ? payload : payload.data || [];
      } catch (e) {
        this.campaigns = [];
      } finally {
        this.campaignsLoading = false;
      }
    },
    campaignDateLabel(c) {
      const value = c.created_at || c.scheduled_at || c.datetime;
      if (!value) return "-";
      return this.$companyTime(value, "YYYY-MM-DD HH:mm", String(value));
    },
    campaignStatusLabel(c) {
      const map = {
        QUEUED: this.$t("passengersPage.mkCampaignQueued"),
        RUNNING: this.$t("passengersPage.mkCampaignRunning"),
        DONE: this.$t("passengersPage.mkCampaignDone"),
        FAILED: this.$t("passengersPage.mkCampaignFailed"),
      };
      return map[c.status] || c.status || "-";
    },
    campaignChannelsLabel(c) {
      const parts = [];
      if (c.send_notification) parts.push(this.$t("passengersPage.channelPush"));
      if (c.send_in_app) parts.push(this.$t("passengersPage.mkChannelInApp"));
      if (c.send_sms) parts.push(this.$t("passengersPage.channelSms"));
      return parts.length ? parts.join(" + ") : "-";
    },
    campaignTargetedLabel(c) {
      const parts = [];
      if (c.passengers_targeted != null) {
        parts.push(`${this.$t("passengersPage.passengers")}: ${c.passengers_targeted}`);
      }
      if (c.drivers_targeted != null) {
        parts.push(`${this.$t("passengersPage.drivers")}: ${c.drivers_targeted}`);
      }
      return parts.length ? parts.join(" · ") : "-";
    },
    deliveryStatsLabel(c) {
      const s = c.delivery_stats;
      if (!s) return "-";
      const parts = [];
      if (s.sent) parts.push(`${this.$t("passengersPage.mkStatusSent")}: ${s.sent}`);
      if (s.pending) parts.push(`${this.$t("passengersPage.mkStatusPending")}: ${s.pending}`);
      if (s.failed) parts.push(`${this.$t("passengersPage.mkStatusFailed")}: ${s.failed}`);
      if (s.expired) parts.push(`${this.$t("passengersPage.mkStatusExpired")}: ${s.expired}`);
      return parts.length ? parts.join(" · ") : "-";
    },
    campaignMessageLabel(c) {
      const title = c.title ? `${c.title}: ` : "";
      return `${title}${c.message || "-"}`;
    },
    filtersSummary(filters) {
      if (!filters || typeof filters !== "object") {
        return this.$t("passengersPage.filtersSummaryNone");
      }
      const f = filters;
      if (f.targeting_mode === "ALL") {
        return this.$t("passengersPage.mkSendToAll");
      }
      if (f.targeting_mode === "SPECIFIC") {
        const count =
          (Array.isArray(f.selected_passenger_ids) ? f.selected_passenger_ids.length : 0) +
          (Array.isArray(f.selected_captain_ids) ? f.selected_captain_ids.length : 0);
        return this.$t("passengersPage.mkSpecificUsersCount", { count });
      }
      const parts = [];
      if (f.registered_from) parts.push(`${this.$t("passengersPage.registeredFrom")}: ${f.registered_from}`);
      if (f.registered_to) parts.push(`${this.$t("passengersPage.registeredTo")}: ${f.registered_to}`);
      if (f.registered_within_days) {
        parts.push(`${this.$t("passengersPage.registeredFrom")}: -${f.registered_within_days}d`);
      }
      if (f.activity) {
        const label =
          f.activity === "active"
            ? this.$t("passengersPage.mkActivityActive")
            : this.$t("passengersPage.mkActivityInactive");
        parts.push(`${this.$t("passengersPage.mkActivity")}: ${label} (${f.activity_days || 30})`);
      }
      // Legacy campaigns recorded before the redesign.
      if (f.min_orders) parts.push(`${this.$t("passengersPage.minBookings")}: ${f.min_orders}`);
      if (f.passenger_type) parts.push(f.passenger_type);
      if (f.platform) parts.push(f.platform);
      if (f.language) parts.push(f.language);
      if (f.inactive_days) parts.push(`${this.$t("passengersPage.inactiveDays")}: ${f.inactive_days}`);
      if (f.voucher_code) parts.push(`${this.$t("passengersPage.voucherCode")}: ${f.voucher_code}`);
      if (f.has_used_voucher) parts.push(this.$t("passengersPage.hasUsedVoucher"));
      if (f.captain_status) parts.push(f.captain_status);
      return parts.length ? parts.join(", ") : this.$t("passengersPage.filtersSummaryNone");
    },
  },
  mounted() {
    this.simulateCount();
    this.loadTemplates();
    this.loadCampaigns();
  },
  beforeDestroy() {
    if (this.countTimer) clearTimeout(this.countTimer);
  },
};
</script>

<style scoped>
.headline {
  font-weight: 500;
}
/* Selectable audience cards (replaces v-radio-group; the dot is pure CSS so
   no icon-font glyph can go missing, and it renders identically in RTL). */
.mode-option {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}
.mode-option:hover {
  border-color: #bdbdbd;
}
.mode-option.mode-active {
  border-color: #4caf50;
  background-color: #f7fbf3;
}
.mode-head {
  display: flex;
  align-items: center;
}
.mode-dot {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border: 2px solid #9e9e9e;
  border-radius: 50%;
  margin-inline-end: 12px;
  position: relative;
  transition: border-color 0.2s;
}
.mode-option.mode-active .mode-dot {
  border-color: #4caf50;
}
.mode-option.mode-active .mode-dot::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  right: 3px;
  bottom: 3px;
  background: #4caf50;
  border-radius: 50%;
}
.mode-title {
  font-size: 15px;
  font-weight: 500;
}
.mode-option.mode-active .mode-title {
  color: #2e7d32;
}
.mode-desc {
  margin-top: 8px;
  margin-inline-start: 32px;
  color: #757575;
  font-size: 13px;
}
.mode-content {
  margin-top: 12px;
  margin-inline-start: 32px;
  cursor: default;
}
.field-col {
  padding: 0 8px;
}
.audience-types {
  border-top: 1px solid #eeeeee;
  margin-top: 4px;
  padding-top: 12px;
}
.selected-users-box {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #e0e0e0;
}
.search-results {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  max-height: 260px;
  overflow-y: auto;
  margin-top: 4px;
  background: #ffffff;
}
.tile-action-row {
  flex-direction: row;
  align-items: center;
}
.campaign-history {
  width: 100%;
  border-collapse: collapse;
}
.campaign-history th,
.campaign-history td {
  padding: 6px 10px;
  text-align: start;
  font-size: 13px;
  vertical-align: top;
}
</style>
